import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import styles from '../assets/seance-grid.module.scss';
import { Paginated } from 'src/services/api-handlers/pagination';
import Grid, { Column } from './Grid';
import textFilter from 'src/components/react-table/filters/text-filter';
import dropDownFilter from 'src/components/react-table/filters/dropdown-filter';
import { ISeance } from 'src/types/seance';
import { QueryParams } from 'src/types/grid';
import { CellProps } from 'react-table';

import dateFilter from '../../../../../../../components/react-table/filters/date-filter';

import { IOrder } from '../../../../../../../types/order';
import { getValFromEnum } from '../../../../../../../components/redux-form/utils/dropdown-values';
import { withoutHiddenStatuses } from 'src/helpers/seance/seanceStatus';

import SeanceEnum from 'src/enumerables/seance-enum';
import { changeToZeroMoment } from '../../../../../../../helpers/timeHelpers';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import Router from '../../../../../../../navigation/router';
import { routes } from '../../../../../../../navigation';
import DataSheetLabel from '../../../../../../../components/common/DatasheetLabel';
import user from '../../../../../../../services/user-auth';
import UserRole from '../../../../../../../enumerables/user-role';
import dateFilterMulti from '../../../../../../../components/react-table/filters/date-filter-multi';
import dateSeanceDateFilterMulti from '../filters/date-seance-date-filter-multi';
import ConfirmInTimeAction from './ConfirmInTimeAction';

interface Props {
    data: Paginated<ISeance>;
    inProgressCSV: boolean;
    getData: (params: QueryParams) => void;
    getDataCsv: (params: QueryParams) => void;
    providers: SelectObject[];
    students: SelectObject[];
    districts: SelectObject[];
    vendors: SelectObject[];
    provider?: any;
}

const statusEnum = SeanceEnum ? SeanceEnum.getEnum('SeanceStatus') : {};
const statusItems: SelectObject[] = getValFromEnum(statusEnum);

const dataSheetEnum = SeanceEnum ? SeanceEnum.getEnum('SeanceFilterDatasheet') : {};
const dataSheetItems: SelectObject[] = getValFromEnum(dataSheetEnum);

const inTimeFilterValues: SelectObject[] = [
    {
        label: 'yes',
        value: 1,
    },
    {
        label: 'no',
        value: 2,
    },
];

const getColumns = (
    data: SelectObject[],
    dataStud: SelectObject[],
    vendors: SelectObject[],
    districts: SelectObject[],
): Column<ISeance>[] => {
    const isProvider = user.getRoles().includes(UserRole.ROLE_USER);
    const isSupervisor = user.getSupervisor();
    const isJustProvider = isProvider && !isSupervisor;
    const columnData: Column<ISeance>[] = [
        {
            Header: 'Note ID',
            accessor: 'id', //occasion
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                const dataSheet: string = original.datasheet.join(',');
                return isJustProvider && original['signDoc'] && dataSheet !== 'Waiting' ? (
                    <a rel="noopener noreferrer" target="_blank" href={`${original['signDoc']}`}>
                        Note
                    </a>
                ) : original?.provider ? (
                    <Link
                        className={'btn btn-info'}
                        to={Router.generate(routes.PROVIDER_DETAIL, {
                            subId: original['id'],
                            id: original?.provider?.id,
                            tab: 'case-sessions-edit',
                        })}
                        target="_blank"
                    >
                        {original['id']}
                    </Link>
                ) : (
                    original['id']
                );
            },
            disableSortBy: true,
            Filter: textFilter<ISeance>(),
        },

        {
            Header: 'Student',
            accessor: 'student.id',
            disableSortBy: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) =>
                original?.student ? `${original?.student?.firstName} ${original?.student?.lastName}` : '',
            Filter: dropDownFilter<ISeance>(dataStud, styles.minWidth),
        },
        {
            Header: 'Supervisor',
            accessor: 'supervisor',
            disableSortBy: true,

            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                if (Object.values(original.supervisor).length) {
                    return original.supervisor.fullname;
                }
                return null;
            },
            Filter: dropDownFilter<ISeance>(data, styles.minWidth),
        },

        {
            Header: 'Case',
            accessor: 'occasion',
            disableSortBy: true,
            disableFilters: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                if (!original?.occasion) return '';
                return (
                    <Link
                        to={Router.generate(routes.CASE_EDIT, {
                            id: original?.occasion?.id,
                        })}
                    >
                        {original.occasion?.caseNumber}
                    </Link>
                );
            },
        },
        {
            Header: 'Service',
            accessor: 'serviceType',
            disableSortBy: true,
            disableFilters: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                let title = original?.serviceType?.title;
                if (!title) {
                    title = original?.serviceType?.cpt;
                }
                return title;
            },
        },
        {
            Header: 'Location',
            accessor: 'serviceLocation',
            disableSortBy: true,
            disableFilters: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                const locationArr: string[] = [];
                locationArr.push(original['serviceLocation'] ? original['serviceLocation']?.title : '');
                if (original.isHome) {
                    locationArr.push('[Home - In Person]');
                }
                if (original.isInPerson) {
                    locationArr.push('[School - In Person]');
                }
                if (original.isTelehealth) {
                    locationArr.push('[Telehealth]');
                }
                if (original.isSchool) {
                    locationArr.push('[School - Telehealth]');
                }
                return locationArr.join(', ');
            },
        },
        {
            Header: 'Date',
            accessor: 'seanceDate',
            disableSortBy: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => changeToZeroMoment(original.seanceDate, 'MMM D, YYYY'),
            headerClassName: 'text-center w-100 d-block',
            Filter: dateSeanceDateFilterMulti<ISeance>(),
        },

        {
            Header: 'Start',
            accessor: 'startTime',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => changeToZeroMoment(original.startTime, 'LT'),
            disableSortBy: true,
            disableFilters: true,
            Filter: dateFilter<ISeance>(),
        },
        {
            Header: 'End',
            accessor: 'endTime',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => changeToZeroMoment(original.endTime, 'LT'),
            disableSortBy: true,
            disableFilters: true,
            Filter: dateFilter<ISeance>(),
        },
        {
            Header: 'PBook',
            disableSortBy: true,
            accessor: 'datasheet',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                const dataSheet: string = original.datasheet.join(',');
                return <DataSheetLabel text={dataSheet} />;
            },
            Filter: dropDownFilter<IOrder>(dataSheetItems, styles.minWidth),
        },
        {
            Header: 'Status',
            disableSortBy: true,
            accessor: 'status',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                const valueCaseStatus = statusItems.find((it) => it.value === original.status);
                return valueCaseStatus ? valueCaseStatus.label : '';
            },
            Filter: dropDownFilter<IOrder>(withoutHiddenStatuses(statusItems), styles.minWidth),
        },
        {
            Header: 'Confirmation Date',
            disableSortBy: true,
            accessor: 'confirmationDate',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) =>
                original.confirmationDate ? changeToZeroMoment(original.confirmationDate, 'MMM D, YYYY') : '',
            headerClassName: 'text-center w-100 d-block',
            Filter: dateFilterMulti<ISeance>(),
        },
        {
            Header: 'Confirmed On Time',
            disableSortBy: true,
            accessor: 'isConfirmInTime',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {

                return <ConfirmInTimeAction data={original} />
            },
            Filter: dropDownFilter<ISeance>(inTimeFilterValues, styles.minWidth),
        },
        {
            Header: 'Vendor',
            accessor: 'vendor.id',
            disableSortBy: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                if (!original?.vendor) return '';

                return (
                    <Link
                        to={Router.generate(routes.VENDOR_DETAIL, {
                            id: original?.vendor?.id,
                        })}
                    >
                        {`${original.vendor?.name}`}
                    </Link>
                );
            },
            Filter: dropDownFilter<ISeance>(vendors, styles.minWidth),
        },
        {
            Header: 'District',
            disableSortBy: true,
            accessor: 'district.id',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                if (!original?.district) return '';

                return (
                    <Link
                        to={Router.generate(routes.DISTRICT_DETAIL, {
                            id: original?.district?.id,
                        })}
                    >
                        {`${original.district?.name}`}
                    </Link>
                );
            },
            Filter: dropDownFilter<ISeance>(districts, styles.minWidth),
        },
    ];

    return columnData;
};

const SeancesGrid: React.FunctionComponent<Props> = ({
    data,
    vendors,
    districts,
    getData,
    providers,
    inProgressCSV,
    getDataCsv,
    provider,
    students,
}: Props) => {
    //const columns: Column<ISeance>[] = getColumns(providers);

    const [columns, setColumns] = useState<any>([]);

    if (providers.length && students.length && !columns.length) {
        setColumns(getColumns(providers, students, vendors, districts));
    }
    /* useEffect(() => {
        if (providers.length && students.length) {
            setColumns(getColumns(providers, students, vendors, districts));
        }
        // eslint-disable-next-line
    }, [columnOpen]);*/

    const match: any = useRouteMatch();
    const filtersInit: any = [];
    const locationObject: any = useLocation();
    const query = new URLSearchParams(locationObject.search);

    if (query.get('status')) {
        match.params.status = query.get('status');
    }
    if (query.get('datasheet')) {
        match.params.datasheet = query.get('datasheet');
    }
    // if (query.get('datasheet')) {
    // match.params.provider.id = match.params.id;
    // }
    useEffect(() => {
        if ((Object.values(match.params).length && match.params.datasheet) || match.params.datasheet) {
            Object.keys(match.params).forEach((k) => {
                filtersInit.push({ id: k, value: +match.params[k] });
            });
        }
        if ((Object.values(match.params).length && match.params.status) || match.params.status) {
            Object.keys(match.params).forEach((k) => {
                filtersInit.push({ id: k, value: +match.params[k] });
            });
        }
        // filtersInit.push({ id: 'provider.id', value: +match.params.id });
    }, []); // eslint-disable-line

    return (
        <div className={classNames(styles.grid)}>
            <Grid<ISeance>
                columns={columns}
                inProgressCSV={inProgressCSV}
                data={data}
                providerId={provider}
                getDataCsv={getDataCsv}
                getData={getData}
                filtersInit={filtersInit}
            />
        </div>
    );
};

export default SeancesGrid;
